import React, { useEffect, useState } from 'react';
import { Card } from 'reactstrap';
import * as Icon from 'react-feather';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { useDebouncedCallback } from 'use-debounce';

import AddEditProcessModal from './AddEditProcessModal';
import AdminHeader from '../../../components/header/AdminHeader';
import CallexLoader from '../../../components/CallexLoader';
import Pagination from '../../../components/form/Pagination';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';
import whatsappIcon from '../../../assets/images/icons/whatsapp.svg';
import CopyProcessModal from './CopyProcessModal';

import {
  processListGet,
  useProcessList,
  processDelete,
  processStatusUpdate,
  updateProcessList,
  auditorStatusUpdate
} from '../../../store/slices/tenantSlices/configurationSlice';
import AddViewCrmsModal from './AddViewCrmsModal';
import AssignQueueProcessModal from './AssignQueueProcessModal';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission } from '../../../utils/common';
import AssignAuditorProcessModal from './AssignAuditorProcessModal';
import AssignWhatsappProcessModal from './AssignWhatsappProcessModal';
import AssignDialableStatusModal from './AssignDialableStatusModal';
import { Tooltip } from 'react-tooltip';
import AssignWhatsappMetaProcessModal from './AssignWhatsappMetaProcessModal';

// Only a time based process has a calling window. Shown as HH:mm like the process form.
const formatTimeWindow = (val) =>
  val?.time_zone === 'yes' && val?.start_time && val?.end_time
    ? `${val.start_time.slice(0, 5)} - ${val.end_time.slice(0, 5)}`
    : '-';

const Process = () => {
  const dispatch = useDispatch();
  const processList = useProcessList();
  const userRolePermission = useUserRolePermission();

  const [openAddModal, setOpenAddModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [openCrmAddModal, setOpenCrmAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);
  const [processId, setProcessId] = useState(null);
  const [processAuditorId, setProcessAuditorId] = useState(null);
  const [processDispositionId, setProcessDispositionId] = useState(null);
  const [processWhatsappId, setProcessWhatsappId] = useState(null);
  const [processMetaId, setProcessMetaId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({
    page: 1,
    limit: 10,
    search: undefined
  });

  const fetchProcessList = useDebouncedCallback(async () => {
    setIsLoading(true);
    try {
      const payload = { ...filter };
      await dispatch(processListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  }, 500);

  const deleteProcess = async () => {
    try {
      const response = await dispatch(processDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        if (processList?.data?.length === 1) {
          setFilter({ ...filter, page: filter?.page > 1 ? filter.page - 1 : 1 });
        } else {
          fetchProcessList();
        }
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const onStatusChange = async (val) => {
    try {
      const payload = {
        id: val.id,
        process_status: !val?.active
      };
      const response = await dispatch(processStatusUpdate(payload)).unwrap();
      if (response) {
        toast.success('Status updated successfully');
        const updatedData = processList?.data?.map((item) =>
          item.id === val.id ? { ...item, active: !val.active } : item
        );
        const updatedProcessList = { ...processList, data: updatedData };
        dispatch(updateProcessList(updatedProcessList));
      }
    } catch (error) {
      console.error('Error updating process status:', error);
      toast.error('Failed to update status');
    }
  };

  const onAuditStatusChange = async (val) => {
    try {
      const payload = {
        process_id: val.id,
        is_auto_audit: !val?.is_auto_audit
      };
      const response = await dispatch(auditorStatusUpdate(payload)).unwrap();
      if (response) {
        toast.success('Status updated successfully');
        const updatedData = processList?.data?.map((item) =>
          item.id === val.id ? { ...item, is_auto_audit: !val.is_auto_audit } : item
        );
        const updatedProcessList = { ...processList, data: updatedData };
        dispatch(updateProcessList(updatedProcessList));
      }
    } catch (error) {
      console.error('Error updating process status:', error);
      toast.error('Failed to update status');
    }
  };

  useEffect(() => {
    fetchProcessList();
  }, [filter]);

  return (
    <div>
      <AdminHeader
        title="Process"
        isExpandSearch
        isAdd
        isCopy
        copyBtnName="Copy Process"
        onAddClick={() => setOpenAddModal(true)}
        onCopyClick={() => setOpenCopyModal(true)}
        isAddDisabled={!checkModulePermission('is_add', 'Process', userRolePermission)}
        addBtnName="Add Process"
        searchPlaceholder="Search Process"
        search={filter.search}
        handelSearch={(e) => setFilter({ ...filter, page: 1, search: e.target.value })}
      />
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : processList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Process', userRolePermission) ? (
        <Card className="p-0 position-relative m-0">
          <div className="daily-report-table" style={{ height: 'calc(100vh - 210px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>CRM</th>
                  <th>Type</th>
                  <th className="text-center">Dial Level</th>
                  <th title="Outbound Routes">Route</th>
                  <th className="text-center" title="Time Based">
                    Timing
                  </th>
                  <th className="text-center" style={{ width: '120px' }}>
                    Active
                  </th>
                  <th className="text-center" style={{ width: '120px' }}>
                    Active Auditor
                  </th>
                  {checkModulePermission('is_view', 'Queues', userRolePermission) && (
                    <th className="text-center" style={{ width: '90px' }} title="Assign Queue">
                      Queue
                    </th>
                  )}
                  {checkModulePermission('is_view', 'Disposition', userRolePermission) && (
                    <th
                      className="text-center"
                      style={{ width: '90px' }}
                      title="Assign Disposition">
                      Disposition
                    </th>
                  )}
                  {checkModulePermission('is_view', 'Whatsapp Meta', userRolePermission) && (
                    <th className="text-center" style={{ width: '90px' }} title="Assign Meta">
                      Meta
                    </th>
                  )}
                  <th className="text-center" style={{ width: '150px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {processList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.id}</td>
                      <td>{val?.name || '-'}</td>
                      <td>{val?.crm_name || '-'}</td>
                      <td>{val?.type || '-'}</td>
                      <td className="text-center">{val?.dial_level || '-'}</td>
                      <td>{val?.outboundroutes_name || '-'}</td>
                      <td className="text-center">{formatTimeWindow(val)}</td>
                      <td
                        style={{ background: val?.active ? '#2cabe333' : '#ff505033' }}
                        className="cursor-pointer"
                        onClick={() => onStatusChange(val)}>
                        {val?.active ? (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-info">
                            <Icon.StopCircle size={16} />
                            Running
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-danger">
                            <Icon.PlayCircle size={16} />
                            Stopped
                          </div>
                        )}
                      </td>
                      <td
                        style={{ background: val?.is_auto_audit ? '#2cabe333' : '#ff505033' }}
                        className="cursor-pointer"
                        onClick={() => onAuditStatusChange(val)}>
                        {val?.is_auto_audit ? (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-info">
                            <Icon.StopCircle size={16} />
                            Running
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2 justify-content-center text-danger">
                            <Icon.PlayCircle size={16} />
                            Stopped
                          </div>
                        )}
                      </td>
                      {checkModulePermission('is_view', 'Queues', userRolePermission) && (
                        <td>
                          <div className="d-flex justify-content-around align-items-center">
                            {val?.type &&
                            ![
                              'OutboundAndPredictive',
                              'OutboundAndPreview',
                              'OutboundAndProgressive'
                            ].includes(val?.type) ? (
                              <Icon.UserPlus
                                className="cursor-pointer"
                                size={16}
                                onClick={() => setProcessId(val?.id)}
                              />
                            ) : (
                              '-'
                            )}
                          </div>
                        </td>
                      )}
                      {checkModulePermission('is_view', 'Disposition', userRolePermission) && (
                        <td>
                          <div className="d-flex justify-content-around align-items-center">
                            <Icon.CheckSquare
                              className="cursor-pointer"
                              size={16}
                              onClick={() => setProcessDispositionId(val?.id)}
                            />
                          </div>
                        </td>
                      )}
                      {checkModulePermission('is_view', 'Whatsapp Meta', userRolePermission) && (
                        <td>
                          <div className="d-flex justify-content-around align-items-center">
                            <Icon.UserCheck
                              className="cursor-pointer"
                              size={16}
                              onClick={() => setProcessMetaId(val?.id)}
                            />
                          </div>
                        </td>
                      )}
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission('is_edit', 'Process', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            } `}>
                            <span
                              data-tooltip-id={`tooltip-edit-${val?.id}`}
                              data-tooltip-content="Edit"
                              className="cursor-pointer">
                              <Icon.Edit
                                size={16}
                                onClick={() => {
                                  if (
                                    checkModulePermission('is_edit', 'Process', userRolePermission)
                                  ) {
                                    setEditRecord(val);
                                    setIsEdit(true);
                                    setOpenAddModal(true);
                                  }
                                }}
                              />
                            </span>
                            <Tooltip id={`tooltip-edit-${val?.id}`} place="top" />
                          </div>
                          <div className="cursor-pointer">
                            <Icon.Users size={16} onClick={() => setProcessAuditorId(val?.id)} />
                          </div>
                          {checkModulePermission('is_view', 'Whatsapp', userRolePermission) && (
                            <div className="cursor-pointer">
                              <img
                                src={whatsappIcon}
                                width={16}
                                height={16}
                                className="cursor-pointer"
                                onClick={() => setProcessWhatsappId(val?.id)}
                              />
                            </div>
                          )}
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission('is_delete', 'Process', userRolePermission)
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission('is_delete', 'Process', userRolePermission)
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={processList?.total}
              limit={processList?.limit}
              page={processList?.page}
              onLimitChange={(val) => {
                setFilter({ ...filter, page: 1, limit: val });
              }}
              onPageClick={(val) => setFilter({ ...filter, page: val })}
              lastPageClick={(val) => setFilter({ ...filter, page: val })}
              firstPageClick={(val) => setFilter({ ...filter, page: val })}
              onRefreshClick={() => fetchProcessList()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        isCommon={false}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteProcess()}
      />
      <AddEditProcessModal
        isOpen={openAddModal}
        setOpenCrmAddModal={setOpenCrmAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            fetchProcessList();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
      <AddViewCrmsModal
        isOpen={openCrmAddModal}
        onCloseClick={() => {
          setOpenCrmAddModal(false);
        }}
      />
      <AssignQueueProcessModal
        isOpen={!!processId}
        process_id={processId}
        onCloseClick={() => setProcessId(null)}
      />
      <AssignAuditorProcessModal
        isOpen={!!processAuditorId}
        process_id={processAuditorId}
        onCloseClick={() => setProcessAuditorId(null)}
      />
      <AssignDialableStatusModal
        isOpen={!!processDispositionId}
        process_id={processDispositionId}
        onCloseClick={() => setProcessDispositionId(null)}
      />
      <AssignWhatsappProcessModal
        isOpen={!!processWhatsappId}
        process_id={processWhatsappId}
        onCloseClick={() => setProcessWhatsappId(null)}
      />
      <AssignWhatsappMetaProcessModal
        isOpen={!!processMetaId}
        process_id={processMetaId}
        onCloseClick={() => setProcessMetaId(null)}
      />
      {openCopyModal && (
        <CopyProcessModal
          isOpen={openCopyModal}
          onCloseClick={(apiCall = false) => {
            setOpenCopyModal(false);
            if (apiCall) {
              fetchProcessList();
            }
          }}
        />
      )}
    </div>
  );
};

export default Process;
